import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { AlertTriangle, ImageDown, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Catalog cleanup banner — visible only when one or more products still hold
 * base64 `data:` URIs in their `image_url` field (which inflates
 * /api/products to ~16 MB and times out on mobile).
 *
 * Click "Migrate Now" → backend decodes each, uploads to Emergent Object
 * Storage, and replaces the field with a regular storage URL. The banner
 * disappears once the count returns to 0.
 */
const Base64ImageMigrationBanner = ({ API, onMigrated }) => {
  const [scan, setScan] = useState(null); // { count, total_bytes, items }
  const [busy, setBusy] = useState(false);
  const [doneSummary, setDoneSummary] = useState(null);

  const fetchScan = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API}/api/admin/migrate-base64-product-images/scan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setScan(await r.json());
    } catch (_) {
      // silent — non-blocking diagnostic
    }
  };

  useEffect(() => {
    fetchScan();
  }, []);

  const handleMigrate = async () => {
    setBusy(true);
    setDoneSummary(null);
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API}/api/admin/migrate-base64-product-images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = await r.json();
      setDoneSummary(result);
      toast.success(`Migrated ${result.migrated} product image${result.migrated === 1 ? '' : 's'}`);
      await fetchScan();
      onMigrated?.();
    } catch (e) {
      toast.error(`Migration failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Hidden when there's nothing to clean and no recent run to summarise.
  if (!scan || (scan.count === 0 && !doneSummary)) return null;

  const mb = (bytes) => (bytes / 1024 / 1024).toFixed(2);

  return (
    <div
      className="mb-6 p-4 rounded-xl border border-amber-300 bg-amber-50"
      data-testid="base64-migration-banner"
    >
      {scan.count > 0 ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900" data-testid="base64-migration-count">
                {scan.count} product{scan.count === 1 ? ' is' : 's are'} carrying inline base64 images ({mb(scan.total_bytes)} MB total).
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                These inflate the catalog payload and slow mobile loads. Migrating moves them to proper image storage.
              </p>
            </div>
          </div>
          <Button
            onClick={handleMigrate}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="base64-migration-run"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Migrating…
              </>
            ) : (
              <>
                <ImageDown className="h-4 w-4 mr-2" /> Migrate Now
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-3" data-testid="base64-migration-done">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <p className="text-sm font-medium text-green-800">
            Catalog cleanup complete — {doneSummary?.migrated || 0} image{doneSummary?.migrated === 1 ? '' : 's'} migrated. Refresh the storefront to verify.
          </p>
        </div>
      )}
    </div>
  );
};

export default Base64ImageMigrationBanner;
